import { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import { useNavigate } from 'react-router-dom';
import type { TreePersonData } from '@antree/shared';
import { cn } from '@/lib/utils';

interface PersonNodeProps {
  data: TreePersonData & { projectId?: string };
  selected?: boolean;
}

function PersonNodeComponent({ data, selected }: PersonNodeProps) {
  const navigate = useNavigate();

  const handleClick = () => {
    if (data.projectId) {
      navigate(`/projects/${data.projectId}/persons/${data.id}`);
    }
  };

  const formatName = () => {
    const parts: string[] = [];
    if (data.firstName) parts.push(data.firstName);
    if (data.lastName) parts.push(data.lastName);
    return parts.length > 0 ? parts.join(' ') : 'Unbekannt';
  };

  const formatLifespan = () => {
    const birth = data.birthDate ? data.birthDate.substring(0, 4) : '?';
    const death = data.deathDate ? data.deathDate.substring(0, 4) : '';
    if (death) {
      return `${birth} - ${death}`;
    }
    if (data.birthDate) {
      return `* ${birth}`;
    }
    return null;
  };

  const genderColors = {
    MALE: 'border-blue-400 bg-blue-50',
    FEMALE: 'border-pink-400 bg-pink-50',
    OTHER: 'border-purple-400 bg-purple-50',
    UNKNOWN: 'border-gray-400 bg-gray-50',
  };

  const genderColor = data.gender ? genderColors[data.gender] : genderColors.UNKNOWN;

  return (
    <>
      {/* Top handle for parent connections */}
      <Handle
        type="target"
        position={Position.Top}
        className="!bg-bronze-500 !w-3 !h-3"
      />

      <div
        onClick={handleClick}
        className={cn(
          'px-4 py-3 rounded-lg border-2 cursor-pointer transition-all min-w-[140px]',
          'hover:shadow-lg hover:scale-105',
          genderColor,
          selected && 'ring-2 ring-bronze-500 ring-offset-2'
        )}
      >
        <div className="text-center">
          <p className="font-medium text-sm truncate max-w-[120px]">
            {formatName()}
          </p>
          {data.birthName && data.birthName !== data.lastName && (
            <p className="text-xs text-muted-foreground truncate">
              geb. {data.birthName}
            </p>
          )}
          {formatLifespan() && (
            <p className="text-xs text-muted-foreground mt-1">
              {formatLifespan()}
            </p>
          )}
        </div>
      </div>

      {/* Bottom handle - used for single parent or direct child connections */}
      <Handle
        type="source"
        position={Position.Bottom}
        id="bottom"
        className="!bg-bronze-500 !w-3 !h-3"
      />

      {/* Left handle for spouse/union connections */}
      <Handle
        type="source"
        position={Position.Left}
        id="left"
        className="!bg-bronze-500 !w-2 !h-2"
      />

      {/* Right handle for spouse/union connections */}
      <Handle
        type="source"
        position={Position.Right}
        id="right"
        className="!bg-bronze-500 !w-2 !h-2"
      />
    </>
  );
}

export const PersonNode = memo(PersonNodeComponent);
